// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xHeaderActions, xHeaderState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface GlobalWebsiteMessageStyles {
    container: InjectableCss;
    close: ButtonPresentationProps;
    textStyles: TypographyPresentationProps;
}

export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    container: {
        css: css`
            text-align: center;
            position: relative;
            padding: 10px 0;
            display: flex;
            justify-content: center;
            align-items: center;

            ${({ globalMessageState }: any) => !globalMessageState && `display: none;`}
        `,
    },
    close: {
        css: css`
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
        `,
    },
    textStyles: {
        css: css`
            &&& {
                text-align: center;
                font-size: ${fontConfig.fontSize.xSmall}px;
                line-height: ${fontConfig.lineHeight.singleLinexSmall};
                color: ${getColor("text.main")};
                margin-bottom: 0;
                padding-right: 20px;
                ${xScreen("lte", "md")} {
                    font-size: ${fontConfig.fontSize.legend}px;
                }
            }
        `,
    },
};

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    globalMessage = "globalMessage",
}

const mapStateToProps = (state: xHeaderState) => ({
    isSticky: state.xHeader.isSticky,
    globalMessageState: state.xHeader.globalMessage,
});

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<xHeaderActions> & {
        fields: {
            [fields.globalMessage]: string;
        };
    };

const GlobalWebsiteMessage: React.FC<Props> = ({
    fields: { globalMessage },
    dispatch,
    isSticky,
    globalMessageState,
}) => {
    const onClose = () => {
        dispatch({ type: "xHeader/globalMessage", globalMessage: false });
    };
    return globalMessage?.length ? (
        <StyledWrapper {...globalWebsiteMessageStyles.container} {...({ isSticky, globalMessageState } as any)}>
            <Clickable
                {...globalWebsiteMessageStyles.close}
                onClick={onClose}
                data-test-selector="global_sitemessage_close"
                aria-label="close"
            >
                <Icon src="Clear" size={12} color="common.ui1" />
            </Clickable>
            <Typography {...globalWebsiteMessageStyles.textStyles}>
                {globalMessage && parse(globalMessage, parserOptions)}
            </Typography>
        </StyledWrapper>
    ) : (
        <></>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Global Website Message",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.globalMessage,
            displayName: "Global Message Content",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "your content goes here",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(GlobalWebsiteMessage),
    definition,
};

export default widgetModule;
